<template>
  <div class="approval-tab">
    <!-- TKP-102 / md. 23–32 — gönderen tarafı ("Taslaklarım"): kendi paketleri, düzenleme listesi (yazar adıyla), onaylayan seçimi, gönder / geri çek.
         TKP-153 / tunesoft2 s.3: onaylayan tarafı (TKP-103) "Onayıma Sunulan Taslaklar" sekmesine, periyodik güncelleme (TKP-105) kendi ekranına taşındı. -->

    <div v-if="isLoading" class="text-muted">{{ $t("common.loading") }}</div>

    <div v-else-if="packages.length === 0" class="text-muted" data-test="empty">{{ $t("approval.empty") }}</div>

    <b-card v-for="pkg in packages" :key="pkg._id" class="mb-3" :data-test="`package-${pkg.status}`">
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <!-- TKP-155 / tunesoft2 s.4: "Taslak Adı: #BirimAdı_GGAAYY#" (sistem atar); eski pakette birim adı -->
            <span class="text-muted">{{ $t("approval.package-name") }}:</span> <strong data-test="package-name">{{ packageTitle(pkg, departmentName(pkg.department)) }}</strong>
            <b-badge :variant="pkg.status === 'SUBMITTED' ? 'warning' : 'secondary'" class="ml-2">{{ $t(`approval.status.${pkg.status}`) }}</b-badge>
            <!-- TKP-134: kademe (birim onayı / Uyum onayı) ve birimin onay modu -->
            <b-badge v-if="stageOf(pkg)" variant="info" class="ml-1" data-test="stage">{{ $t(stageLabelKey(stageOf(pkg))) }}</b-badge>
            <small v-if="pkg.status === 'SUBMITTED'" class="ml-2 text-muted">{{ $t("approval.approver") }}: {{ pkg.approverName || "—" }}</small>
            <small v-if="pkg.mode || pkg.draftMode" class="ml-2 text-muted" :data-test="`mode-${pkg.mode || pkg.draftMode}`">{{ $t("approval.mode-label") }}: {{ $t(modeLabelKey(pkg.mode || pkg.draftMode)) }}</small>
          </div>
        </div>
      </template>

      <!-- TKP-155 / tunesoft2 s.4 satır tablosu (TKP-156: bileşen, tarihçede salt okunur da kullanılır) -->
      <draft-rows-table :edits="pkg.edits" :removable="pkg.status === 'OPEN'" :busy="isBusy" @remove="removeEdit" @remove-many="removeEdits" />
      <div class="text-right mt-2">
        <!-- TKP-158 / tunesoft2 s.8: düzenleme istenen satır düzeltilmeden ya da kaldırılmadan gönderilmez -->
        <small v-if="canSubmit(pkg) && openEditRequests(pkg) > 0" class="text-warning mr-2" data-test="submit-blocked">{{ $t("approval.submit-blocked-requests") }}</small>
        <b-button v-if="canSubmit(pkg)" size="sm" variant="primary" :disabled="isBusy || openEditRequests(pkg) > 0" data-test="submit" @click="openSubmit(pkg)">{{ $t("approval.submit") }}</b-button>
        <b-button v-if="canWithdraw(pkg)" size="sm" variant="outline-danger" :disabled="isBusy" data-test="withdraw" @click="withdraw(pkg)">{{ $t("approval.withdraw") }}</b-button>
      </div>

      <div v-if="othersEdits(pkg.department).length" class="mt-3">
        <small class="text-muted">{{ $t("approval.others-in-scope") }}</small>
        <ul class="mb-0 small" data-test="others">
          <li v-for="edit in othersEdits(pkg.department)" :key="edit._id">
            {{ $t(`approval.entity-type.${edit.entityType}`) }} · {{ $t(`approval.operation.${edit.operation}`) }} · {{ edit.authorName || "—" }} ({{ $t(`approval.status.${edit.packageStatus}`) }})
          </li>
        </ul>
      </div>
    </b-card>

    <!-- TKP-156 / tunesoft2 s.4: Tarihçe — gönderilmiş ve tamamlanmış taslaklarım (açılır/kapanır, varsayılan kapalı) -->
    <approval-history scope="mine" />

    <!-- TKP-134: mod'a göre gönderim — bildirim modunda "Yayınla" (onaylayan yok), ön onayda birim onaylayanı da seçilir -->
    <b-modal v-model="submitDialog.show" :title="submitDialog.mode === 'NOTIFY' ? $t('approval.publish') : $t('approval.submit')" :ok-title="submitDialog.mode === 'NOTIFY' ? $t('approval.publish') : $t('approval.submit')" :cancel-title="$t('common.cancel')" :ok-disabled="!submitReady(submitDialog) || isBusy" @ok="submit">
      <!-- TKP-237 m.3: geçerli mod — "Organizasyon Onay Modu - …" ya da "Birim Bazında Onay Modu - …" -->
      <p class="font-weight-bold" :data-test="`mode-hint-${submitDialog.mode}`">{{ $t(`approval.mode-source.${submitDialog.modeSource}`) }} - {{ $t(modeLabelKey(submitDialog.mode)) }}</p>
      <template v-if="submitDialog.mode !== 'NOTIFY'">
        <b-form-group v-if="submitDialog.mode === 'PRE_APPROVAL'" :label="$t('approval.department-approver')">
          <b-form-select v-if="submitDialog.departmentCandidates.length" v-model="submitDialog.departmentApprover" :options="departmentApproverOptions" data-test="department-approver-select" />
          <div v-else class="text-warning small" data-test="department-stage-skipped">{{ $t("approval.department-stage-skipped") }}</div>
        </b-form-group>
        <!-- TKP-237 m.4: "Onaya Gönderilen Kişi" + bilgi işareti -->
        <b-form-group>
          <template #label>{{ $t("approval.approver-to") }} <i class="bx bx-info-circle text-info" data-test="approver-hint" v-b-tooltip.hover :title="$t('approval.approver-hint')"></i></template>
          <b-form-select v-model="submitDialog.approver" :options="approverOptions" data-test="approver-select" />
        </b-form-group>
        <div v-if="submitDialog.candidates.length === 0" class="text-danger small" data-test="no-approver">{{ $t("approval.no-approver") }}</div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import ApprovalHistory from "./approval-history";
import DraftRowsTable from "./draft-rows-table";
import { canSubmit, canWithdraw, fixedScopeKey, modeLabelKey, myPackages, openEditRequests, othersEdits, packageTitle, stageLabelKey, stageOf, submitDialogFrom, submitPayload, submitReady } from "./approval";

export default {
  name: "approval-tab",
  components: { ApprovalHistory, DraftRowsTable },
  data() {
    return { submitDialog: { show: false, packageId: null, mode: "STANDARD", approver: null, candidates: [], departmentApprover: null, departmentCandidates: [] } };
  },
  computed: {
    drafts() {
      return this.$store.getters["approval/drafts"];
    },
    isLoading() {
      return this.$store.getters["approval/isLoading"];
    },
    isBusy() {
      return this.$store.getters["approval/isBusy"];
    },
    packages() {
      return myPackages(this.drafts, this.currentAccount && this.currentAccount._id);
    },
    approverOptions() {
      return this.submitDialog.candidates.map((c) => ({ value: c.account, text: c.email ? `${c.name} (${c.email})` : c.name }));
    },
    departmentApproverOptions() {
      return this.submitDialog.departmentCandidates.map((c) => ({ value: c.account, text: c.email ? `${c.name} (${c.email})` : c.name }));
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    packageTitle,
    canSubmit,
    openEditRequests,
    canWithdraw,
    stageOf,
    stageLabelKey,
    modeLabelKey,
    submitReady,
    othersEdits(department) {
      return othersEdits(this.drafts, department, this.currentAccount && this.currentAccount._id);
    },
    departmentName(departmentId) {
      const fixed = fixedScopeKey(departmentId); // TKP-138: başvuru / veri ihlali kapsamı
      if (fixed) return this.$t(fixed);
      const pagination = this.$store.getters["organizationDepartment/pagination"];
      const found = ((pagination && pagination.docs) || []).find((d) => String(d._id) === String(departmentId));
      const translate = Vue.filter("translate");
      return found ? (translate ? translate(found.name) : found.name.tr) : this.$t("approval.department");
    },
    async load() {
      try {
        await this.$store.dispatch("approval/fetch");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async openSubmit(pkg) {
      try {
        const { data } = await this.$store.dispatch("approval/approvers", pkg._id);
        this.submitDialog = submitDialogFrom(pkg._id, data);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async submit() {
      try {
        await this.$store.dispatch("approval/submit", submitPayload(this.submitDialog));
        this.$toast.success(this.$t(this.submitDialog.mode === "NOTIFY" ? "approval.published" : "approval.submitted"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async withdraw(pkg) {
      try {
        await this.$store.dispatch("approval/withdraw", pkg._id);
        this.$toast.success(this.$t("approval.withdrawn"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    /** TKP-237 t.8: toplu işlemin satırları birlikte kaldırılır. */
    /** TKP-242 q.3: toplu işlemin satırları tek istekte (eskiden satır başına istek + her birinde taslak listesi yenilemesi). */
    async removeEdits(edits) {
      try {
        await this.$store.dispatch("approval/removeEdits", (edits || []).map((e) => e._id));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async removeEdit(edit) {
      try {
        await this.$store.dispatch("approval/removeEdit", edit._id);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>
